/**
 * Route paths for the marketing section, in one place.
 *
 * WHY THEY LIVE HERE AND NOT IN mobile.tsx
 * ----------------------------------------
 * They used to be exported from mobile.tsx, which is also where the screens are
 * imported and the routes assembled. That was fine until a screen needed to
 * link to another screen: DoorList linking to a door's detail page would import
 * MARKETING_PATHS from mobile.tsx, while mobile.tsx imports DoorList -- a cycle.
 *
 * ES module cycles involving plain constants often "work", right up until the
 * evaluation order changes and the constant is `undefined` at first read. The
 * resulting bug is a link to `/undefined/123`, produced by code that looks
 * correct. A leaf module with no imports of its own cannot participate in a
 * cycle at all.
 *
 * mobile.tsx re-exports this, so the existing import path keeps working.
 */
export const MARKETING_PATHS = {
  hub: "/marketing",
  overview: "/marketing",
  doors: "/marketing/doors",
  door: "/marketing/doors/:id",
  review: "/marketing/review",
  drops: "/marketing/drops",
  // Kept only so existing links and bookmarks still land somewhere. Schedule
  // and Drops were merged; this path redirects rather than 404s.
  scheduleLegacy: "/marketing/schedule",
  materials: "/marketing/materials",
  // The same story as scheduleLegacy. The content library moved to
  // contactContent below and this path only redirects there now, so it is
  // named for what it is: `content` sitting one word away from
  // `contactContent` read as two interchangeable constants when only one is
  // a destination.
  contentLegacy: "/marketing/content",
  /**
   * Where one contact's touch history used to live. It moved to
   * CONTACT_TOUCHES_PATH below, for the reason that constant records, and
   * this path redirects there rather than 404ing.
   *
   * NOT INTERCHANGEABLE WITH THE TWO LEGACY PATHS ABOVE. scheduleLegacy and
   * contentLegacy are static-to-static, so their routes are a bare
   * `<Navigate to={...}>`. This one has to carry the `:id` out of the old URL
   * and into the new one, and `<Navigate to>` does no interpolation --
   * handing it "/contacts/:id/touches" navigates to a URL with a literal
   * colon in it, which matches no route and 404s exactly as this path does
   * today. Its route reads the param and builds the destination with
   * touchesPath. See mk-touches-legacy in mobile.tsx.
   */
  touchesLegacy: "/marketing/touches/:id",
  campaign: "/marketing/campaign",
  docs: "/marketing/docs",
  // The contacts section. DELIBERATELY under /contacts rather than
  // /marketing, for the reason CONTACT_TOUCHES_PATH records below: Header.tsx
  // picks the active top-level tab by matching the path, so a follow-up queue
  // living at /marketing/followups would light **Pipeline** and sit under the
  // pipeline's own sub-navigation. A URL is a claim about where you are.
  followups: "/contacts/followups",
  sequences: "/contacts/sequences",
  // One sequence and its steps. Nested under the list rather than sitting
  // beside it, because a step means nothing outside the cadence it belongs to
  // and the URL should say which one you are editing.
  sequence: "/contacts/sequences/:id",
  // Where the content library lives; contentLegacy above redirects here. The
  // library holds email, script and note bodies sent to PEOPLE, and filing it
  // under a section about door-to-door mailing runs meant nobody looked there
  // for it.
  contactContent: "/contacts/content",
  // The outbound email queue, and the controls over it. Under /contacts for
  // the reason followups is: Header.tsx picks the active top-level tab by
  // matching the path, so a queue of messages to PEOPLE living at
  // /marketing/outbound would light **Pipeline** and sit under the pipeline's
  // door-to-door sub-navigation. A URL is a claim about where you are.
  outbound: "/contacts/outbound",
  /**
   * The lifecycle screen with NO contact chosen: a search that says, for each
   * result, whether they are enrolled and where they are -- so it doubles as
   * "who am I working right now".
   *
   * A STATIC SEGMENT SITTING BESIDE A DYNAMIC ONE. This and
   * CONTACT_LIFECYCLE_PATH below are `/contacts/lifecycle` and
   * `/contacts/:id/lifecycle`. React Router v6 ranks a static segment above a
   * dynamic one, so `/contacts/lifecycle` reaches the picker whatever order
   * they are registered in -- but they are registered adjacently anyway,
   * because depending on ranked matching without saying so is how the next
   * person reorders the list and silently breaks it.
   */
  lifecycle: "/contacts/lifecycle",
  /**
   * The settings behind the lifecycle: the cadence, the default next action
   * and the display order of every stage, per track.
   *
   * UNDER /contacts FOR THE REASON followups AND lifecycle ARE, and not under
   * /marketing: Header.tsx picks the active top-level tab by matching the
   * path, so settings about how PEOPLE are chased living at
   * /marketing/anything would light **Pipeline** and wear the door-to-door
   * sub-navigation.
   *
   * A SIBLING OF `lifecycle`, NOT A CHILD OF IT. `/contacts/lifecycle/stages`
   * would read more neatly and would be wrong: ContactsShell's matchesScreen
   * is a prefix test, so that path is also claimed by the Lifecycle tab, and
   * activePath takes the FIRST match in CONTACTS_SCREENS order -- so one of
   * the two tabs could never light. The hyphen keeps the two paths disjoint,
   * which is the only thing that check reads.
   *
   * NOT UNDER /settings EITHER. Upstream owns that route for its own
   * configuration page, and a fork adding rows to somebody else's settings
   * screen is merge surface in exactly the kind of file this fork keeps
   * injections out of -- for a screen that belongs beside the queues it
   * governs.
   */
  lifecycleStages: "/contacts/lifecycle-stages",
} as const;

/**
 * Is this path anywhere inside the marketing section?
 *
 * Lives HERE, in the leaf module, rather than beside the button that uses it:
 * mobile.tsx imports every pipeline screen, so a Node test of this predicate
 * would drag the whole screen tree into a project that cannot render.
 *
 * NOT a bare `startsWith(hub)`, which is what the two nav components used to
 * do separately. `/marketing` is a prefix of `/marketingfoo`, and more to the
 * point the boundary is the thing being asserted -- a predicate that cannot
 * tell a segment boundary from a substring is one route name away from
 * lighting the wrong entry.
 *
 * Docs IS in the section. It sits under /marketing for historical reasons
 * (see `docs` above), and the mobile bar now has ONE entry covering both the
 * pipeline tabs and Docs, so the mobile predicate must not exclude it. The
 * desktop header still splits them into two tabs and keeps its own `onDocs`
 * test; that split is about which of TWO tabs lights, not about whether the
 * section is active, so the two are not duplicates of each other.
 */
export const isMarketingSection = (pathname: string) =>
  isAtOrBelow(pathname, MARKETING_PATHS.hub);

/**
 * Is `pathname` this path, or anything below it?
 *
 * THE ONE SEGMENT-BOUNDARY RULE, written once. Every "am I inside section X"
 * question in this fork is this test, and the failure mode when it is written
 * by hand is always the same: a bare `startsWith(path)` also claims
 * `/marketingfoo` and `/contactsfoo`, so a route added next year one letter
 * away from an existing one silently lights somebody else's entry. Appending
 * the slash is what makes it a segment test rather than a substring test.
 *
 * ContactsShell.tsx called this `matchesScreen` and owned the only copy; it
 * moved here when the mobile bar needed the same rule, because a second copy
 * beside a second lamp is how a bar and a tab strip come to disagree about
 * where you are.
 */
export const isAtOrBelow = (pathname: string, path: string) =>
  pathname === path || pathname.startsWith(`${path}/`);

/**
 * The Contacts section root.
 *
 * NOT IN `MARKETING_PATHS`, because it is upstream's route rather than one of
 * ours -- we only hang a sub-navigation and a mobile menu off it. It is in
 * this file all the same, so the predicates below have exactly one spelling
 * of it to test against.
 */
export const CONTACTS_ROOT = "/contacts";

/**
 * Pages that BELONG to a Contacts tab but do not live under /contacts.
 *
 * WHY THIS EXISTS, AND WHY IT IS NOT OPTIONAL. The Content library is at
 * /contacts/content, but its rows are react-admin resource records:
 * `rowClick="edit"` navigates to /content_assets/5, which shares NO prefix
 * with /contacts. The desktop tab bar once tested only that prefix, so
 * opening a template made the whole sub-navigation vanish -- and the way back
 * was the browser's back button, because the tabs that would have taken you
 * there were the thing that disappeared.
 *
 * The mobile bottom bar asks the same question for its Contacts slot, and a
 * prefix test there reintroduces exactly that bug one lamp over: the slot goes
 * dark on a page that is plainly a contacts page. Both read `ownedTab` below.
 *
 * A LIST, NOT A LOOSER PREFIX TEST. Matching on something vaguer would claim
 * pages that genuinely are not contacts pages; naming the two or three
 * resource routes a tab owns keeps the rule readable and keeps the pipeline's
 * own screens out of it. Adding a tab whose records live in a resource route
 * is one line here, in the same commit as the tab.
 */
const OWNED_ELSEWHERE: ReadonlyArray<readonly [string, string]> = [
  ["/content_assets", MARKETING_PATHS.contactContent],
];

/** The Contacts tab a resource route belongs to, or null if it belongs to none. */
export const ownedTab = (pathname: string): string | null =>
  OWNED_ELSEWHERE.find(([prefix]) => isAtOrBelow(pathname, prefix))?.[1] ??
  null;

/**
 * Is this path anywhere inside the Contacts section?
 *
 * The union of "under /contacts" and "a resource route a Contacts tab owns",
 * and BOTH halves are load-bearing. Drop the second and /content_assets/5 --
 * a template opened from the Content library -- stops counting as a contacts
 * page: the desktop tab strip disappears and the mobile Contacts slot goes
 * dark, on the same tap, for the same reason.
 *
 * Lives here rather than beside either navigation for the reason
 * isMarketingSection does: this is pure string work, so the leaf module makes
 * it assertable in the Node project, and one exported predicate is what stops
 * the bar and the tab strip drifting apart. contactsSection.test.ts asserts it
 * in both directions; mobileNav.test.tsx asserts only that it is WIRED to the
 * lamp, which is the half Node cannot see.
 */
export const isContactsSection = (pathname: string) =>
  isAtOrBelow(pathname, CONTACTS_ROOT) || ownedTab(pathname) !== null;

/**
 * Every touch for one contact. The contact page shows only the newest few; a
 * person worked for a year has a history that would otherwise bury their own
 * record.
 *
 * DELIBERATELY NOT UNDER /marketing. It lived there first, and the header
 * highlighted **Pipeline** while the pipeline's own sub-navigation sat above
 * a page about one person -- because Header.tsx picks the active tab by
 * matching the path, and PipelineShell is applied to everything in the
 * marketing route list. A URL is a claim about where you are, and that one
 * was false. Under /contacts it needs no special case: `/contacts/*` already
 * lights the right tab.
 */
export const CONTACT_TOUCHES_PATH = "/contacts/:id/touches";

/** Link to one contact's full touch history. */
export const touchesPath = (contactId: string | number) =>
  `/contacts/${contactId}/touches`;

/**
 * One contact's whole lifecycle: their touches, the plan their enrollment
 * will actually walk, and the controls over both.
 *
 * DELIBERATELY NOT UNDER /marketing, for the reason CONTACT_TOUCHES_PATH
 * gives above and with no exception of its own: Header.tsx picks the active
 * top-level tab by matching the path, so a page about one PERSON living under
 * /marketing would light Pipeline and wear the door-to-door sub-navigation.
 *
 * AND NOT A TAB ON THE CONTACT PAGE. The plan is five to fifteen rows, each
 * with its own dates, statuses, task and controls; a panel on a record that
 * also carries notes, tasks and a company would bury the record it sits on.
 * The same argument the touch history settled the same way.
 */
export const CONTACT_LIFECYCLE_PATH = "/contacts/:id/lifecycle";

/** Link to one contact's lifecycle page. */
export const lifecyclePath = (contactId: string | number) =>
  `/contacts/${contactId}/lifecycle`;

/** Link to one sequence and its steps. */
export const sequencePath = (sequenceId: string | number) =>
  `/contacts/sequences/${sequenceId}`;

/**
 * Every task for one contact, open and closed.
 *
 * MIRRORS CONTACT_TOUCHES_PATH EXACTLY, and for the reasons that constant
 * records above: not under /marketing, because Header.tsx picks the active
 * top-level tab by matching the path and a page about one PERSON living there
 * would light Pipeline and wear the door-to-door sub-navigation; and not a
 * panel on the contact page, because a person worked for a year has a task
 * history that would bury their own record.
 *
 * WHY IT IS A SEPARATE SCREEN FROM THE PANEL AT ALL. The owner drew the line
 * himself: "at the current contact detail page, I dont need to see everything
 * -- just the currently open tasks". The panel is what is outstanding; this is
 * the record of what was asked and what was done. One screen doing both does
 * neither job.
 *
 * NOT `/tasks?contact_id=`. That is the global queue filtered, and it loses
 * the two things this page is for: a way back to the person, and a scope that
 * cannot silently widen -- the global queue is scoped to `sales_id`, so a
 * contact worked by somebody else would show a filtered EMPTY list rather
 * than their tasks.
 */
export const CONTACT_TASKS_PATH = "/contacts/:id/tasks";

/** Link to one contact's full task history. */
export const contactTasksPath = (contactId: string | number) =>
  `/contacts/${contactId}/tasks`;

/**
 * The global task queue: my own work, open and completed.
 *
 * NOT IN `MARKETING_PATHS`, for the reason CONTACTS_ROOT is not -- it is
 * upstream's own resource route, minted by `<Resource name="tasks">` in
 * root/CRM.tsx, and the mobile bottom bar has linked to it since long before
 * this fork. Until now the DESKTOP registration carried no `list`, so the same
 * URL resolved to a screen on a phone and to nothing at all on a laptop
 * (atomic#15).
 *
 * Spelled here all the same, so the dashboard link and the contact panel's
 * "see all" share one spelling of it.
 */
export const ALL_TASKS_PATH = "/tasks";
